import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useState } from 'react';
import { Send, Lock, MessageSquare, GitCommit, ExternalLink, User } from 'lucide-react';
import type { JourneyEvent } from '@platform/shared-types';
import { journeyApi } from '../../services/journeyApi';
import { toastService } from '../../services/toastService';
import { useDataVersion } from '../../services/dataChangeBus';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * WHAT HAPPENED, IN ORDER, FOR EVER.
 *
 * Append-only. Nothing here can be edited or deleted, which is the point: this
 * is what a warranty claim, a chargeback or a collection is argued from years
 * after the order shipped, and a history somebody can tidy up is not evidence.
 * A correction is a new entry saying what was corrected.
 *
 * The same component serves three questions — what happened to this ORDER,
 * to this CUSTOMER over years, and to this DEVICE — because they are the same
 * events read through different indexes.
 */

const KIND_ICON: Record<string, React.ReactNode> = {
  stage: <GitCommit size={12} />,
  message: <MessageSquare size={12} />,
  note: <Lock size={12} />,
  external: <ExternalLink size={12} />,
};

const KIND_TONE: Record<string, string> = {
  stage: 'bg-rose-100 text-rose-700',
  message: 'bg-blue-100 text-blue-700',
  note: 'bg-stone-200 text-stone-600',
  external: 'bg-amber-100 text-amber-700',
};

const when = (ts: number) =>
  new Date(ts).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

/**
 * RAW IDS OUT OF THE PROSE — at READ time, never at write time.
 *
 * "Journey opened for order ord_3cec5cd26a6e13a92f6dba6e." is the truth and is
 * stored exactly as written; this record is append-only and stays that way. But
 * the entry is read by a person years later, and a UUID mid-sentence is noise
 * to them. So the stored text is untouched and only its RENDERING swaps the ids
 * the caller could resolve — the order's number, the store's name.
 *
 * Only ids in `labels` are swapped. An id nobody handed us a name for is left
 * alone rather than hidden: an unresolvable reference is still a fact.
 */
const humanise = (text: string, labels?: Record<string, string>): string => {
  if (!text || !labels) return text;
  let out = text;
  // Longest id first, so `loc_17` can never eat a prefix of `loc_1789…`.
  for (const id of Object.keys(labels).sort((a, b) => b.length - a.length)) {
    if (!id || !labels[id]) continue;
    out = out.split(id).join(labels[id]);
  }
  return out;
};

interface Props {
  /** One journey's history. */
  journeyId?: string | null;
  /** Everything one customer has ever done. */
  tenantId?: string;
  /** One device or model — `unit:u_1`, `model:mdl_k10`. */
  subject?: string;
  title?: string;
  /**
   * A second stream folded into this one — `location:loc_1`.
   *
   * A document verified on the Registrations tab happens to the LOCATION, not
   * to this order, so it lives on the location's journey. Reading it in here
   * is what makes "whatever happens in registration shows on the timeline"
   * true without writing the same event to two places.
   */
  mergeSubject?: string;
  /**
   * Raw id → what to show instead, for the prose of each entry. `ord_3cec…` →
   * `RB-106 000 3`, `loc_1789…` → the store's name. See `humanise`: the stored
   * entry never changes, only how it reads.
   */
  labels?: Record<string, string>;
  /** Only a journey can be written to; the aggregate views are read-only. */
  canPost?: boolean;
  limit?: number;
}

const JourneyTimeline: React.FC<Props> = ({ journeyId, tenantId, subject, mergeSubject, title, labels, canPost, limit = 100 }) => {
  const [events, setEvents] = useState<JourneyEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [internal, setInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const dataVersion = useDataVersion();

  const load = useCallback(async () => {
    setError(null);
    try {
      if (journeyId) {
        const [own, merged] = await Promise.all([
          journeyApi.timeline(journeyId, limit),
          // Silent on failure: the order's own history is the point, and a
          // missing location stream must not empty the panel.
          mergeSubject
            ? journeyApi.subjectHistory(mergeSubject, limit).catch(() => ({ timeline: [] as JourneyEvent[] }))
            : Promise.resolve({ timeline: [] as JourneyEvent[] }),
        ]);
        const seen = new Set(own.timeline.map((e) => e.eventId));
        setEvents([...own.timeline, ...merged.timeline.filter((e) => !seen.has(e.eventId))]
          .sort((a, b) => a.at - b.at));
      } else if (tenantId) setEvents((await journeyApi.tenantHistory(tenantId, { limit })).timeline);
      else if (subject) setEvents((await journeyApi.subjectHistory(subject, limit)).timeline);
      else setEvents([]);
    } catch (e: any) {
      setError(e?.message || 'Could not read the history.');
    } finally {
      setLoading(false);
    }
  }, [journeyId, tenantId, subject, mergeSubject, limit, dataVersion]); // dataVersion: re-read after any save on the page

  useEffect(() => { load(); }, [load]);

  const post = async () => {
    if (!journeyId || !draft.trim()) return;
    setSending(true);
    try {
      await journeyApi.message(journeyId, draft.trim(), { internal });
      setDraft('');
      await load();
    } catch (e: any) {
      toastService.error(e?.message || 'Could not add that.');
    } finally {
      setSending(false);
    }
  };

  // Newest first for the long views; oldest first reads better for one order,
  // where the point is the sequence rather than what happened lately.
  const ordered = journeyId ? events : [...events].reverse();

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
        <h3 className="text-[11px] uppercase tracking-[0.12em] font-bold text-stone-500">
          {title || 'History'}
        </h3>
        {!loading && <span className="text-[11.5px] text-stone-400">{events.length} entries</span>}
      </div>

      {loading && (
        <RubyLoader variant="section" />
      )}

      {error && <p className="text-[12.5px] text-amber-700">{error}</p>}

      {!loading && !error && events.length === 0 && (
        <p className="text-[12.5px] text-stone-400">Nothing recorded yet.</p>
      )}

      <div className="space-y-2.5">
        {ordered.map((e) => (
          <div key={e.eventId} className="flex gap-2.5">
            <span className={`w-6 h-6 rounded-lg grid place-items-center shrink-0 mt-0.5 ${KIND_TONE[e.kind] || 'bg-stone-200 text-stone-600'}`}>
              {KIND_ICON[e.kind] || <GitCommit size={12} />}
            </span>
            <div className="min-w-0 flex-1">
              {/* `title` keeps the entry AS RECORDED within reach — the reader
                  sees the readable form, an auditor can still see the ids. */}
              <p className="text-[12.5px] text-stone-800 leading-relaxed" title={e.summary}>
                {humanise(e.summary, labels)}
              </p>
              {e.body && e.body !== e.summary && (
                <p className="text-[12px] text-stone-600 leading-relaxed mt-0.5 whitespace-pre-wrap" title={e.body}>
                  {humanise(e.body, labels)}
                </p>
              )}
              <p className="text-[11px] text-stone-400 mt-0.5 inline-flex items-center gap-1 flex-wrap">
                <User size={9} />
                {/* The name AS IT WAS — never joined to the current staff record. */}
                {e.actor.name}
                {e.actor.roleId ? ` · ${e.actor.roleId}` : ''}
                {' · '}{when(e.at)}
                {e.kind === 'note' && <span className="ml-1 px-1.5 py-0.5 rounded bg-stone-200 text-stone-500 font-semibold">internal</span>}
              </p>
            </div>
          </div>
        ))}
      </div>

      {canPost && journeyId && (
        <div className="mt-4 pt-3 border-t border-stone-200">
          <div className="flex gap-2 flex-wrap">
            <input
              value={draft}
              onChange={(ev) => setDraft(ev.target.value)}
              onKeyDown={(ev) => { if (ev.key === 'Enter' && draft.trim()) post(); }}
              placeholder="Add to the record…"
              className="flex-1 min-w-[200px] px-3.5 py-2.5 rounded-lg border border-stone-200 text-[12.5px] text-stone-800 focus:outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-500/10"
            />
            <button
              onClick={() => setInternal(!internal)}
              className={`px-3 py-2 rounded-lg text-[11px] font-semibold border ${
                internal ? 'bg-stone-200 border-stone-300 text-stone-700' : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'}`}
              title="An internal note is never shown to the merchant or the partner"
            >
              <Lock size={11} className="inline mr-1" />{internal ? 'Internal' : 'Shared'}
            </button>
            <button
              onClick={post}
              disabled={!draft.trim() || sending}
              className="px-3.5 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-[11px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {sending ? <RubyGemMark size={11} drawing color="currentColor" /> : <Send size={11} />} Add
            </button>
          </div>
          <p className="text-[11px] text-stone-400 mt-2 leading-relaxed">
            Recorded against your name and never editable. A correction is a new entry.
          </p>
        </div>
      )}
    </div>
  );
};

export default JourneyTimeline;
